import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminService } from '../../services/adminService';
import { BLOG_STATUS, formatBlogDate } from '../../utils/blogHelpers';
import '../../styles/admin.css';

const STATUS_LABELS = {
  [BLOG_STATUS.DRAFT]: 'Brouillon',
  [BLOG_STATUS.PUBLISHED]: 'Publié',
  [BLOG_STATUS.SCHEDULED]: 'Programmé'
};

const AdminBlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await adminService.getBlogPosts();
      setPosts(data);
    } catch (error) {
      console.error('Erreur lors du chargement des articles :', error);
      toast.error('Impossible de charger les articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (post) => {
    const confirmed = window.confirm(`Supprimer l'article "${post.title}" ? Cette action est irréversible.`);
    if (!confirmed) return;
    try {
      await adminService.deleteBlogPost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success('Article supprimé');
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'article :', error);
      toast.error('Impossible de supprimer l\'article');
    }
  };

  const handleToggleStatus = async (post) => {
    const nextStatus = post.status === BLOG_STATUS.PUBLISHED ? BLOG_STATUS.DRAFT : BLOG_STATUS.PUBLISHED;
    try {
      const payload = { status: nextStatus };
      if (nextStatus === BLOG_STATUS.PUBLISHED && !post.publishedAt) {
        payload.publishedAt = new Date().toISOString();
      }
      await adminService.updateBlogPost(post.id, { ...post, ...payload });
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, ...payload } : p)));
      toast.success(nextStatus === BLOG_STATUS.PUBLISHED ? 'Article publié' : 'Article dépublié');
    } catch (error) {
      console.error('Erreur lors du changement de statut :', error);
      toast.error('Impossible de changer le statut');
    }
  };

  return (
    <div className="admin-products">
      <div className="container">
        <div className="admin-header">
          <h1>
            <i className="fas fa-newspaper"></i>
            Articles de blog
          </h1>
          <div className="breadcrumb">
            <Link to="/admin/dashboard">
              <i className="fas fa-tachometer-alt"></i> Tableau de bord
            </Link>
            <span>/</span> Blog
          </div>
          <div className="admin-actions">
            <Link to="/admin/blog/add" className="btn btn-primary">
              <i className="fas fa-plus"></i> Nouvel article
            </Link>
          </div>
        </div>

        <div className="products-table-container">
          <h2>
            <i className="fas fa-list"></i> Tous les articles ({posts.length})
          </h2>

          <div className="table-responsive">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"><div className="spinner"></div></div>
                <p>Chargement des articles...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="no-products">
                <i className="fas fa-newspaper"></i>
                <h3>Aucun article pour le moment</h3>
                <p>Commencez à publier du contenu pour attirer du trafic organique.</p>
                <Link to="/admin/blog/add" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                  <i className="fas fa-plus"></i> Créer le premier article
                </Link>
              </div>
            ) : (
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Statut</th>
                    <th>Publication</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id}>
                      <td>
                        <strong>{post.title}</strong>
                        <br />
                        <small>/blog/{post.slug}</small>
                      </td>
                      <td>{post.categoryName || '—'}</td>
                      <td>
                        <span className={`stock-badge ${post.status === BLOG_STATUS.PUBLISHED ? 'in-stock' : 'out-of-stock'}`}>
                          {STATUS_LABELS[post.status] || post.status}
                        </span>
                      </td>
                      <td>{post.publishedAt ? formatBlogDate(post.publishedAt) : '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleToggleStatus(post)}
                            title={post.status === BLOG_STATUS.PUBLISHED ? 'Dépublier' : 'Publier'}
                          >
                            <i className={`fas ${post.status === BLOG_STATUS.PUBLISHED ? 'fa-eye-slash' : 'fa-upload'}`}></i>
                          </button>
                          <Link to={`/admin/blog/${post.id}/edit`} className="btn btn-sm btn-outline" title="Modifier">
                            <i className="fas fa-edit"></i>
                          </Link>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(post)} title="Supprimer">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogList;
